import React, {useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import _ from "lodash";
import {isMobile} from "../../../App";
import paths from "../../../paths";
import {auth} from "../../../store/auth.store";
import {CurrencyFormat, preventBetSlipValues} from "../../../services/misc.functions";
import {modalServiceSubject} from "../../../services/modal.service";
import {
    LongtermBetslip,
    longtermBettingType,
    useLongtermBetslip,
    useLongtermSlipOptions,
} from "features/betslip/longterm.service";
import {CouponImage, resolveCouponImage, startCouponImageCapture} from "../coupon-image";
import "../../../shared/betslip/betslip.css";
import iconTrash from "../../../assets/feather/trash-2.svg";
import iconRedTrash from "../../../assets/feather/trash2-red.svg";
import iconSave from "../../../assets/feather/save.svg";
import iconClose from "../../../assets/feather/x-circle.svg";
import iconLeft from "../../../assets/feather/chevron-left.svg";
import infoDangerIcon from "../../../assets/feather/info-danger.svg";

const BET_SLIP_MAX_MULTIPLY = 12000;
const MAX_WIN_ERROR = "Maksimum kazanç tutarı 7.500.000 ₺'den fazla olamaz";

function buildMultiplyChoices(): number[] {
    let choices: number[] = [];
    if (BET_SLIP_MAX_MULTIPLY > 200) {
        choices = _.range(1, 101);
        const mod = BET_SLIP_MAX_MULTIPLY % 50;
        choices.push(..._.range(200, BET_SLIP_MAX_MULTIPLY, 50));
        if (mod > 0) {
            choices.push(choices[choices.length - 1] + mod);
        }
    } else {
        choices = _.range(1, BET_SLIP_MAX_MULTIPLY + 1);
    }
    return choices;
}

const multiplyChoices = buildMultiplyChoices();

export function NewLongtermBetslip({onClose}: { onClose?: () => void }) {
    const navigate = useNavigate();
    const selection = useLongtermBetslip();
    const options = useLongtermSlipOptions();

    const hippodrome = selection?.hippodrome ?? null;
    const totalAmount = LongtermBetslip.getTotalAmount();
    const maxWin = LongtermBetslip.getMaxWin();
    const errorClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => {
        if (errorClearTimer.current) clearTimeout(errorClearTimer.current);
    }, []);

    const scheduleErrorClear = () => {
        if (errorClearTimer.current) clearTimeout(errorClearTimer.current);
        errorClearTimer.current = setTimeout(() => LongtermBetslip.setOptions({errorMessage: null}), 5000);
    };

    useEffect(() => {
        if (maxWin > 7_500_000) {
            if (options.errorMessage !== MAX_WIN_ERROR) {
                LongtermBetslip.setOptions({errorMessage: MAX_WIN_ERROR});
            }
        } else if (options.errorMessage === MAX_WIN_ERROR) {
            LongtermBetslip.setOptions({errorMessage: null});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxWin]);

    const playCoupon = async (save: boolean = false, imgData: CouponImage = "") => {
        if (options.inProgress || !selection) return;

        if (!auth.member || !auth.member.id) {
            modalServiceSubject.next({
                title: "Üye Girişi",
                content: `<p>Bahis yapabilmeniz için önce oturum açmanız gerekli. Üye değilseniz hemen üye olabilir yada giriş yaparak bahis yapabilirsiniz.</p>`,
                confirm: {
                    sure: {label: "Giriş Yap", class: "btn btn-success", action: () => navigate(paths.login.url)},
                    cancel: {label: "Üye Ol", class: "btn btn-primary", action: () => navigate(paths.register.url)},
                },
            });
            return;
        }

        const processingModalTimeout = setTimeout(() => {
            modalServiceSubject.next({
                case: "info",
                title: "Kuponunuz Oynanıyor",
                content: '<div class="text-center"><div class="loader"></div><p>Kuponunuz oynanmak üzere United Racing sistemine iletildi. United Racing sisteminden yanıt gelene kadar lütfen bekleyiniz.</p></div>',
                confirm: null,
                backdropCloseDisabled: true,
            });
        }, 100);

        const result = await LongtermBetslip.submit(save, await resolveCouponImage(imgData));
        clearTimeout(processingModalTimeout);

        if (!result) {
            setTimeout(() => {
                modalServiceSubject.next(false);
            }, 1500);
            return;
        }

        if (result.status === true && save === false) {
            try {
                // @ts-ignore
                fbq("track", "Purchase", {value: totalAmount, currency: "TRY"});
            } catch (e) {
            }
            LongtermBetslip.clear();
            modalServiceSubject.next({
                case: "info",
                title: "Kuponunuz Oynandı",
                content: `<p class="text-center"><b>${result.data.coupon_data.approve_code
                }</b> kodu ile <b>${CurrencyFormat(Number(result.data.coupon_data.amount || 0))} ₺</b> tutarındaki <b>${longtermBettingType?.name
                }</b> kuponunuz başarıyla oynandı. <br/>Kuponunuzun durumunu kuponlarım sayfasından takip edebilirsiniz.</p>`,
                confirm: {
                    sure: {label: "At Yarışı Kuponlarım", action: () => navigate(paths.memberTickets.url)},
                    cancel: {label: "Yeni Kupon Oyna", action: () => { if (isMobile) onClose?.(); }},
                },
            });
        } else if (result.status && save === true) {
            modalServiceSubject.next({
                case: "info",
                title: "Kuponunuz Kaydedildi",
                content: "Kuponunuz başarıyla Kaydedildi. Kuponunuzu kuponlarım sayfasında görüntüleyebilirsiniz",
                confirm: {
                    sure: {label: "Kayıtlı Kuponlarım", action: () => navigate(paths.memberSavedTickets.url)},
                    cancel: {label: "Yeni Kupon Oyna", action: () => { if (isMobile) onClose?.(); }},
                },
            });
        } else {
            let errorDescription: any = null;
            let errorMessageCode: any = null;
            if (result.data && result.data.error) {
                errorDescription = result.data.error.error_description;
                errorMessageCode = result.data.error.error_message;
            }

            let title: any = "Hata";
            let sure: any = null;
            if (typeof result.message === "string" && result.message.indexOf("Oturum") !== -1) {
                sure = {label: "Giriş Yap", action: () => navigate(paths.login.url)};
            }
            if (errorMessageCode === "TIMEOUT") {
                title = "Dikkat";
                sure = {label: "At Yarışı Kuponlarım", action: () => navigate(paths.memberTickets.url)};
            }

            modalServiceSubject.next({
                case: "danger",
                title: title,
                content: errorDescription || (typeof result.message === "object" ? JSON.stringify(result.message) : result.message),
                confirm: {sure, cancel: {label: "Tamam"}},
            });
        }
    };

    const confirmCoupon = async () => {
        if (!(auth.authentication && auth.member && auth.member.id)) {
            navigate(`${paths.login.url}/${window.location.pathname.replaceAll("/", "|")}`);
            return;
        }

        let errorMessage: string | null = null;

        if (!selection) {
            errorMessage = "Kuponunuzda oynanacak bir bahis bulunmamaktadır.";
        } else if (
            options.shareCoupon !== true &&
            (options.usePointBalance
                ? Number(auth.member.balance_debit) + Number(auth.member.balance_point)
                : auth.member.balance_debit) < Number(totalAmount)
        ) {
            errorMessage = "Bakiyeniz yetersiz.";
        } else if (Number(totalAmount) < (longtermBettingType?.minCouponAmount ?? 20)) {
            errorMessage = "Minumum bahis tutarı " + CurrencyFormat(longtermBettingType?.minCouponAmount ?? 20) + " TL olmalıdır.";
        }

        if (errorMessage) {
            LongtermBetslip.setOptions({errorMessage});
            scheduleErrorClear();
            return;
        }

        // Görüntü onay modalı açılmadan ÖNCE başlatılıyor (modal görüntüye girmesin) ama
        // BEKLENMİYOR: anlık görüntü çağrı anında alınıyor, geri kalanı modal açıkken bitiyor.
        const imgData = startCouponImageCapture("betslip-body");

        if (isMobile) {
            playCoupon(false, imgData);
            return;
        }

        modalServiceSubject.next({
            title: `BAHİS DETAYI - ${hippodrome?.hippodrome || ""}`,
            width: "960px",
            content: `
                <div class="coupon-legs" style="width:auto; margin-top: 15px;">
                    <div class="col-12 px-2 my-2">
                        <div class="run-no">${hippodrome?.hippodrome || ""}</div>
                        <div class="horse-no justify-content-between align-items-center d-flex">
                            <div style="font-weight: bold">${selection?.horse.description}</div>
                            <div class="text-left ps-2">${Number(selection?.horse.win_odds).toFixed(2)}</div>
                        </div>
                    </div>
                </div>
                 <div class="row">
                  <div class="col-12">
                      <p class="my-1 alert alert-warning p-2 fs-7 fw-bold">
                        Uzun Vadeli bahis türünde oynanan kuponlar iptal edilemez. Bilet oynanırken tüm oranlar değişebilir.
                       </p>
                  </div>
                  <div class="col-6 d-flex justify-content-around fw-bold fs-6">
                    <div>Toplam Tutar:</div>
                    <div>${CurrencyFormat(totalAmount)}₺</div>
                  </div>
                 <div class="col-6 d-flex justify-content-around fw-bold fs-6">
                    <div>Maks. Kazanç:</div>
                    <div>${CurrencyFormat(maxWin)}₺</div>
                  </div>
                  </div>
      `,
            confirm: {
                sure: {class: "btn-success", label: "Onayla", action: () => playCoupon(false, imgData)},
                cancel: {label: "Vazgeç"},
            },
        });

        scheduleErrorClear();
    };

    return (
        <div className="betslip" id={"betslip"}>
            {isMobile && (
                <button
                    className="btn btn-primary btn-icon float-start py-1 px-1 me-2"
                    onClick={() => onClose?.()}
                >
                    <img alt={"left"} src={iconLeft} className="invert-color me-1"/>
                </button>
            )}

            <h2 className="betting-type">
                {longtermBettingType?.name}
                {!isMobile && onClose && (
                    <img alt={"kapat"} src={iconClose} className="float-end cursor-pointer" onClick={() => onClose()}/>
                )}
            </h2>

            <div className="body betslip-body flex-column" id={"betslip-body"}>
                {selection && hippodrome ? (
                    <div className={"card fob-slip-item mt-2"}>
                        <div className={"fw-bold d-flex justify-content-between"}>
                            {hippodrome.hippodrome}
                            <div className={"mb-2 me-1"}>
                                <img
                                    alt={"sil"}
                                    src={iconTrash}
                                    width={16}
                                    onClick={() => LongtermBetslip.clear()}
                                    className="cursor-pointer float-right"
                                />
                            </div>
                        </div>
                        <div className={"card-body"}>
                            <div className={"d-flex justify-content-between"}>
                                <div className={"d-flex align-items-center fw-bold"}>
                                    {selection.horse.description}
                                </div>

                                <div className={"d-flex flex-row align-items-end"}>
                                    <div className={"fw-bold odd-button selected"}>
                                        {Number(selection.horse.win_odds).toFixed(2)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="hint-text text-center p-3">
                        <p className={"fs-6 mb-0"}>Bahis yapmak için bir oran seçiniz.</p>
                    </div>
                )}

                <div className={"d-flex mt-2 justify-content-center align-items-center text-danger"}>
                    <img src={infoDangerIcon} className={"me-1"} alt={"info"}/>
                    <span className={"lh-1 fs-7 fw-medium"}>
                        Uzun Vadeli bahis türünde oynanan kuponlar iptal edilemez.
                    </span>
                </div>
            </div>

            <div className="footer">
                <div>
                    <label>Misli:</label>
                    <div className={"betslip-misli"}>
                        <input
                            type="number"
                            className={"betslip-input"}
                            style={{textAlign: "left"}}
                            pattern="^[0-9\b]+$"
                            min={20}
                            max={BET_SLIP_MAX_MULTIPLY}
                            step={1}
                            value={options.multiply || ""}
                            onKeyDown={preventBetSlipValues}
                            onChange={(e) => LongtermBetslip.setOptions({multiply: Number(e.target.value)})}
                            onBlur={() => {
                                if (Number(options.multiply) < 20) {
                                    LongtermBetslip.setOptions({multiply: 20});
                                } else if (Number(options.multiply) > BET_SLIP_MAX_MULTIPLY) {
                                    LongtermBetslip.setOptions({multiply: BET_SLIP_MAX_MULTIPLY});
                                }
                            }}
                            onPaste={(e) => {
                                e.preventDefault();
                                return false;
                            }}
                        />
                        <select
                            className="betslip-select"
                            value={options.multiply}
                            onChange={(e) => LongtermBetslip.setOptions({multiply: Number(e.target.value)})}
                        >
                            {multiplyChoices.map((el: number, key: number) => (
                                <option key={key} value={el}>
                                    {el}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                    <label>Tutar:</label>
                    <span>{CurrencyFormat(totalAmount)}₺</span>
                </div>

                <div>
                    <label>Maks. Kazanç:</label>
                    <span>{CurrencyFormat(maxWin)}₺</span>
                </div>

                {auth.authentication && auth.member && auth.member.id ? (
                    <div>
                        <label>Puan Kullan:</label>
                        <span>
                            <input
                                type="checkbox"
                                checked={options.usePointBalance}
                                disabled={auth.member ? auth.member.balance_point <= 0 : true}
                                onChange={() => LongtermBetslip.setOptions({usePointBalance: !options.usePointBalance})}
                            />{" "}
                            {auth.member ? auth.member.balance_point + "₺" : "0.00₺"}
                        </span>
                    </div>
                ) : null}

                {options.errorMessage ? (
                    <div className="mt-1 mb-1">
                        <div className="w-100 p-2 alert alert-danger m-0">{options.errorMessage}</div>
                    </div>
                ) : null}

                {auth.authentication && auth.member && auth.member.is_editor === true && (
                    <div>
                        <label>Paylaş:</label>
                        <span>
                            <input
                                type="checkbox"
                                checked={options.shareCoupon}
                                onChange={() => LongtermBetslip.setOptions({shareCoupon: !options.shareCoupon})}
                            />
                        </span>
                    </div>
                )}

                <div className={"d-flex"}>
                    <span className={"lh-1 fs-7 fw-medium text-danger w-100"} style={{textAlign: "left"}}>
                        {" "}
                        Bilet oynanırken tüm oranların değişebileceğini kabul et
                    </span>
                    <input
                        type="checkbox"
                        className={"ms-2"}
                        checked={options.acceptChangedOdds}
                        onChange={() => LongtermBetslip.setOptions({acceptChangedOdds: !options.acceptChangedOdds})}
                    />
                </div>

                {options.inProgress ? (
                    <div className="actions">
                        <button className="button-clear" disabled>
                            <img src={iconTrash} alt=""/>
                        </button>
                        <button disabled className="button-save">
                            <img src={iconSave} alt=""/>
                        </button>
                        <button className="button-play" disabled>
                            Hemen Oyna
                        </button>
                    </div>
                ) : (
                    <div className="actions">
                        <button className="button-clear" onClick={() => LongtermBetslip.clear()}>
                            <img src={iconTrash} alt=""/>
                        </button>

                        <button onClick={() => playCoupon(true)} className="button-save" disabled={!selection}>
                            <img src={iconSave} alt=""/>
                        </button>

                        {!options.shareCoupon && (!selection || hippodrome?.status === 1) && (
                            <button
                                disabled={
                                    !selection ||
                                    !options.acceptChangedOdds ||
                                    options.inProgress ||
                                    !!options.errorMessage
                                }
                                className={`button-play`}
                                onClick={() => confirmCoupon()}
                            >
                                Hemen Oyna
                            </button>
                        )}

                        {selection && hippodrome && hippodrome.status !== 1 && (
                            <button className="button-play" disabled>
                                Bahisler Açılmadı
                            </button>
                        )}

                        {options.shareCoupon && (
                            <button
                                disabled={!selection || !options.acceptChangedOdds || !!options.errorMessage}
                                className={`button-play share`}
                                onClick={() => confirmCoupon()}
                            >
                                Hemen Paylaş
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export function NewLongtermMobileBetslipBar({onOpen}: { onOpen: () => void }) {
    const selection = useLongtermBetslip();
    const totalAmount = LongtermBetslip.getTotalAmount();

    return (
        <div id="mobile-foot-betslip" className="mobile-foot-betslip sticky">
            <div className="row">
                <div className="col-4">
                    <div className={"d-flex align-items-center justify-content-between"}>
                        <h2 className={"mb-0"}>BİLETİM</h2>
                    </div>
                </div>
                <div className="col-8 text-end">
                    <button className="btn btn-primary float-end ms-1 fw-bold" onClick={onOpen}>
                        OYNA
                    </button>
                    ORAN:{" "}
                    <>{selection ? Number(selection.horse.win_odds).toFixed(2) : "-"}</>
                    <span className="total d-block fw-bold">
                        TUTAR:
                        <>{CurrencyFormat(totalAmount)}₺</>
                    </span>
                </div>
            </div>

            <div className="row">
                <div className="d-flex py-2 pt-0 w-100 fw-bold fs-6">
                    {selection ? (
                        <div className={"d-flex align-items-center"}>
                            <i className={"d-flex align-content-center"}>
                                <img
                                    alt={"sil"}
                                    src={iconRedTrash}
                                    width={16}
                                    onClick={() => LongtermBetslip.clear()}
                                    className="cursor-pointer float-right me-2"
                                />
                            </i>
                            {selection.horse.description}
                        </div>
                    ) : (
                        <>-</>
                    )}
                </div>
            </div>
        </div>
    );
}
